/**
 * useWindowedRows — thin React wrapper around `WindowedRowCache`.
 *
 * Designed for 100K–1M record tables in App mode where neither holding all rows
 * in memory nor a classic offset-paginator is acceptable. Pairs with TanStack
 * Virtual (or any virtualizer) that asks "what's at row N?".
 *
 * The sparse LRU chunk cache + fetch scheduling itself lives in
 * `./windowedRowCache.ts` (a plain, transport-agnostic class) so that tree
 * grouping (Task 11) can instantiate one cache per expanded group — a hook
 * can't be created dynamically. This hook owns only:
 *   - translating `WindowedRowsParams` into a `fetchPage` closure over `runtimeApi`,
 *   - creating/disposing the cache instance when connection/object/search/sort/
 *     filters/etc. change,
 *   - forcing a re-render (`bump`) whenever the cache reports a change.
 *
 * When the inputs (search / sort / showDeleted / filters / searchFields /
 * objectName / connectionId / chunkSize / maxPagesInMemory) change, the old
 * cache is disposed and a fresh one is created (which re-fetches page 1).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { runtimeApi, type ListResponse } from '@/app/services/runtimeApi'
import { WindowedRowCache, type RowState } from './windowedRowCache'

export type { RowState }

export interface WindowedRowsParams {
  connectionId: string
  objectName: string
  /** Whether the underlying object config has loaded — disables the hook until true. */
  enabled: boolean
  search: string
  sortField: string
  sortDir: 'asc' | 'desc'
  showDeleted: boolean
  /** Whitelisted equality filters (schema tabs/filter_fields), merged server-side with `search`. */
  filters?: Record<string, unknown>
  /** Attribute names `search` should OR-match across (schema `search_fields`). */
  searchFields?: string[]
  /** Server page size. Smaller = more requests + less memory per chunk. */
  chunkSize?: number
  /** LRU cap — number of chunks held in memory at once. */
  maxPagesInMemory?: number
  /** Active i18next locale — resolves translatable reference display server-side. */
  locale?: string
}

export interface WindowedRowsApi {
  /** Authoritative row count from the server (0 until first chunk loads). */
  total: number
  /** Look up a row by absolute index. Triggers chunk fetch as a side effect. */
  getRow: (index: number) => RowState
  /** Reset the cache and reload from page 1. */
  reload: () => void
  /** True while the very first chunk is in flight. */
  loadingInitial: boolean
  /** True while any non-initial chunk fetch is in flight. */
  loadingMore: boolean
  /** Last error message if a fetch failed. */
  error: string | null
  /** Patch a single row in-place (used after PATCH/PUT to avoid a full reload). */
  patchRow: (recordId: string, updater: (row: Record<string, any>) => Record<string, any>) => void
  /** Remove a row by id from the cache (used after delete). */
  removeRow: (recordId: string) => void
}

export function useWindowedRows({
  connectionId,
  objectName,
  enabled,
  search,
  sortField,
  sortDir,
  showDeleted,
  filters,
  searchFields,
  chunkSize = 50,
  maxPagesInMemory = 20,
  locale,
}: WindowedRowsParams): WindowedRowsApi {
  const [, setVersion] = useState(0)
  const bump = useCallback(() => setVersion((v) => v + 1), [])

  const cacheRef = useRef<WindowedRowCache | null>(null)

  const filtersKey = JSON.stringify(filters ?? {})
  const searchFieldsKey = (searchFields ?? []).join(',')

  // Reset + first chunk on actual prop changes (not on callback identity).
  // Note: `search`, `sortField`, `sortDir`, `showDeleted`, `filters`, `searchFields`,
  // `connectionId`, `objectName` are all closed over directly (no paramsRef
  // indirection needed) because every one of them is also a dependency below —
  // if any changed, this effect re-runs and recreates the closure with fresh
  // values; if none changed, the existing closure's values are still current.
  useEffect(() => {
    cacheRef.current?.dispose()
    if (!enabled) {
      // Keep the disposed instance around (not nulled) so `total`/`error`/
      // `loadingInitial`/`loadingMore`/`getRow` keep reading its last-known
      // settled state — matching the original hook's early-return behavior,
      // which left its refs untouched when `enabled` flipped false.
      // `dispose()` stops any further fetches/onChange but does not clear data.
      bump()
      return
    }
    cacheRef.current = new WindowedRowCache({
      chunkSize,
      maxPagesInMemory,
      onChange: bump,
      fetchPage: (page, size) =>
        runtimeApi
          .list(connectionId, objectName, {
            page,
            limit: size,
            search,
            sort_field: sortField,
            sort_dir: sortDir,
            include_deleted: showDeleted,
            filters,
            search_fields: searchFields,
            locale,
          })
          .then((r: ListResponse) => ({ rows: r.data ?? [], total: r.meta?.total ?? 0 })),
    })
    bump()
    return () => cacheRef.current?.dispose()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, connectionId, objectName, search, sortField, sortDir, showDeleted, chunkSize, maxPagesInMemory, filtersKey, searchFieldsKey, locale, bump])

  const getRow = useCallback((index: number): RowState => {
    if (!cacheRef.current) return { state: 'loading' }
    return cacheRef.current.getRow(index)
  }, [])

  const reload = useCallback(() => {
    cacheRef.current?.reload()
  }, [])

  const patchRow = useCallback(
    (recordId: string, updater: (row: Record<string, any>) => Record<string, any>) => {
      cacheRef.current?.patchRow(recordId, updater)
    },
    [],
  )

  const removeRow = useCallback((recordId: string) => {
    cacheRef.current?.removeRow(recordId)
  }, [])

  return {
    total: cacheRef.current?.total ?? 0,
    getRow,
    reload,
    loadingInitial: cacheRef.current?.loadingInitial ?? false,
    loadingMore: cacheRef.current?.loadingMore ?? false,
    error: cacheRef.current?.error ?? null,
    patchRow,
    removeRow,
  }
}
